import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabase";
import styles from "../styles/PasswordReset.module.css";

function PasswordReset() {
  const navigate = useNavigate();

  const [stage, setStage] = useState<"request" | "reset">("request");

  const [email, setEmail] = useState("");
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setStage("reset");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsRequesting(true);

    try {
      const trimmedEmail = email.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        alert("Please enter a valid email address");
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(
        trimmedEmail,
        {
          redirectTo: `${window.location.origin}/password-reset`,
        },
      );

      if (error) throw error;
      setRequestSent(true);
    } catch (error) {
      alert(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsRequesting(false);
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;

      alert("Password updated successfully!");
      navigate("/sign-in");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className={styles["pr-page"]}>
      <div className={styles["pr-card"]}>
        <h1>{stage === "request" ? "Reset Password" : "Set New Password"}</h1>

        <button
          className={styles["pr-back-btn"]}
          onClick={() => navigate("/sign-in")}
        >
          ← Back to Sign In
        </button>

        {/* ── Stage 1: Request reset email ── */}
        {stage === "request" && (
          <>
            {requestSent ? (
              <>
                <p>
                  Check your inbox — we've sent a reset link to{" "}
                  <strong>{email}</strong>.
                </p>
                <p>
                  The link expires in 1 hour. Check spam if you don't see it.
                </p>
                <button
                  className={styles["pr-link-btn"]}
                  onClick={() => {
                    setRequestSent(false);
                    setEmail("");
                  }}
                >
                  Send to a different email
                </button>
              </>
            ) : (
              <>
                <h2>
                  Enter your account email and we'll send you a reset link.
                </h2>

                <div className={styles["pr-field"]}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className={styles["pr-submit-btn"]}
                  disabled={isRequesting}
                  onClick={handleRequestSubmit}
                >
                  {isRequesting ? "Sending..." : "Send Reset Link"}
                </button>
              </>
            )}
          </>
        )}

        {/* ── Stage 2: Set new password ── */}
        {stage === "reset" && (
          <>
            <div className={styles["pr-field"]}>
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
              />
            </div>

            <div className={styles["pr-field"]}>
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <small>Passwords do not match</small>
              )}
            </div>

            <button
              className={styles["pr-submit-btn"]}
              disabled={isResetting}
              onClick={handleResetSubmit}
            >
              {isResetting ? "Updating..." : "Update Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default PasswordReset;
